/**
 * Staff Service - Central service for staff/employee management
 * Integrates with Firebase for real-time data synchronization
 */

import { isDayEnabled } from '../shared/availability';
import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  query,
  where,
  orderBy,
  Timestamp,
  serverTimestamp
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { onAuthStateChanged } from 'firebase/auth';
import { db, storage, auth } from '../config/firebase';
import toast from 'react-hot-toast';
import {
  StaffMember,
  StaffStatus,
  StaffRole,
  CareerStage,
  StaffFilters,
  StaffStatistics,
  TimeOffRequest,
  StaffDocument,
  DEFAULT_AVAILABILITY,
  WeeklyAvailability,
  CompensationDetails,
  StaffMetrics
} from '../types/staff';

/**
 * StaffService class - Singleton pattern
 */
class StaffService {
  private static instance: StaffService;
  // UNIFIED (2026-08-02): `staff` and `barbers` were two parallel collections — `staff` sat
  // empty while every real person lived in `barbers`. This (richer) service now reads the SAME
  // `barbers` collection, so StaffPage, checkout's manager lookup, and reporting all see the
  // real team. One source of truth; the old empty `staff` collection is retired.
  private staffCollection = collection(db, 'barbers');
  private timeOffCollection = collection(db, 'timeOffRequests');
  private documentsCollection = collection(db, 'staffDocuments');
  private listeners: Map<string, () => void> = new Map();
  private cachedStaff: StaffMember[] = [];
  private isInitialized = false;
  private _staffUnsub: (() => void) | null = null;

  private constructor() {
    this.initializeRealTimeSync();
    this.loadFromLocalStorage();
    // RESILIENCE (Jose 2026-10-29 audit): subscribes at module load (pre-auth) → re-subscribe once auth
    // is ready so a denied cold read self-heals (unsub prior first; localStorage already covers offline).
    try { onAuthStateChanged(auth, (u) => { if (u) this.initializeRealTimeSync(); }); } catch { /* ignore */ }
  }

  static getInstance(): StaffService {
    if (!StaffService.instance) {
      StaffService.instance = new StaffService();
    }
    return StaffService.instance;
  }

  // ==================== INITIALIZATION ====================

  private initializeRealTimeSync(): void {
    try { this._staffUnsub?.(); } catch { /* ignore */ }
    const q = query(this.staffCollection, orderBy('lastName', 'asc'));

    this._staffUnsub = onSnapshot(q, (snapshot) => {
      const staffMembers: StaffMember[] = [];

      snapshot.forEach((docSnapshot) => {
        const data = docSnapshot.data();
        staffMembers.push(this.convertFirestoreToStaff(docSnapshot.id, data));
      });

      if (staffMembers.length > 0 || this.isInitialized) {
        this.cachedStaff = staffMembers;
        this.saveToLocalStorage();
        this.isInitialized = true;
      }
    }, (error) => {
      console.error('Staff real-time sync error:', error);
      this.loadFromLocalStorage();
    });
  }

  private convertFirestoreToStaff(id: string, data: any): StaffMember {
    return {
      id,
      firstName: data.firstName || '',
      lastName: data.lastName || '',
      email: data.email || '',
      phone: data.phone || '',
      photo: data.photo,
      dateOfBirth: data.dateOfBirth,
      address: data.address,
      role: data.role || 'barber',
      employmentType: data.employmentType || 'full-time',
      status: data.status || 'active',
      hireDate: data.hireDate?.toDate?.() || new Date(),
      terminationDate: data.terminationDate?.toDate?.(),
      department: data.department,
      skills: data.skills || [],
      certifications: data.certifications || [],
      specialties: data.specialties || [],
      experience: data.experience || 0,
      languages: data.languages || ['English'],
      bio: data.bio,
      availability: data.availability || DEFAULT_AVAILABILITY,
      vacationDays: data.vacationDays || 10,
      sickDays: data.sickDays || 5,
      compensation: data.compensation || this.getDefaultCompensation(),
      careerStage: data.careerStage || 'Barber',
      equity: data.equity,
      metrics: data.metrics || this.getDefaultMetrics(),
      colorCode: data.colorCode,
      notes: data.notes,
      emergencyContact: data.emergencyContact,
      createdAt: data.createdAt?.toDate?.() || new Date(),
      updatedAt: data.updatedAt?.toDate?.() || new Date()
    };
  }

  private getDefaultCompensation(): CompensationDetails {
    return {
      type: 'commission',
      commissionRate: 40,
      bonuses: [],
      paymentMethod: 'direct-deposit',
      payFrequency: 'bi-weekly'
    };
  }

  private getDefaultMetrics(): StaffMetrics {
    return {
      avgRating: 0,
      totalReviews: 0,
      avgServiceTime: 30,
      monthlyRevenue: 0,
      ytdRevenue: 0,
      avgTicket: 0,
      totalClients: 0,
      returningClients: 0,
      clientRetentionRate: 0,
      newClientsThisMonth: 0,
      appointmentsCompleted: 0,
      appointmentsCancelled: 0,
      noShows: 0,
      bpiScore: 0
    };
  }

  // ==================== CRUD OPERATIONS ====================

  /**
   * Get all staff members
   */
  async getAllStaff(): Promise<StaffMember[]> {
    if (this.cachedStaff.length > 0) {
      return this.cachedStaff;
    }

    try {
      const snapshot = await getDocs(this.staffCollection);
      const staffMembers: StaffMember[] = [];

      snapshot.forEach((docSnapshot) => {
        staffMembers.push(
          this.convertFirestoreToStaff(docSnapshot.id, docSnapshot.data())
        );
      });

      this.cachedStaff = staffMembers;
      this.saveToLocalStorage();
      return staffMembers;
    } catch (error) {
      console.error('Error fetching staff:', error);
      return this.cachedStaff;
    }
  }

  /**
   * Get staff member by ID
   */
  async getStaffById(id: string): Promise<StaffMember | null> {
    // Check cache first
    const cached = this.cachedStaff.find(s => s.id === id);
    if (cached) return cached;

    try {
      const docSnapshot = await getDoc(doc(this.staffCollection, id));
      if (!docSnapshot.exists()) return null;

      return this.convertFirestoreToStaff(docSnapshot.id, docSnapshot.data());
    } catch (error) {
      console.error('Error fetching staff by ID:', error);
      return null;
    }
  }

  /**
   * Add new staff member
   */
  async addStaff(staffData: Omit<StaffMember, 'id' | 'createdAt' | 'updatedAt'>): Promise<StaffMember> {
    try {
      const now = Timestamp.now();
      const dataToSave = {
        ...staffData,
        hireDate: staffData.hireDate instanceof Date
          ? Timestamp.fromDate(staffData.hireDate)
          : staffData.hireDate,
        createdAt: now,
        updatedAt: now
      };

      const docRef = await addDoc(this.staffCollection, dataToSave);
      const newStaff: StaffMember = {
        ...staffData,
        id: docRef.id,
        createdAt: now.toDate(),
        updatedAt: now.toDate()
      };

      this.cachedStaff.push(newStaff);
      this.saveToLocalStorage();
      // (No barbers-sync needed — this service now writes directly to the barbers collection.)

      toast.success(`${staffData.firstName} ${staffData.lastName} added successfully`);

      return newStaff;
    } catch (error) {
      console.error('Error adding staff:', error);
      toast.error('Failed to add staff member');
      throw error;
    }
  }

  /**
   * Update staff member
   */
  async updateStaff(id: string, updates: Partial<StaffMember>): Promise<StaffMember | null> {
    try {
      const docRef = doc(this.staffCollection, id);
      const updateData = {
        ...updates,
        updatedAt: serverTimestamp()
      };

      // Convert dates if needed
      if (updates.hireDate instanceof Date) {
        updateData.hireDate = Timestamp.fromDate(updates.hireDate);
      }
      if (updates.terminationDate instanceof Date) {
        updateData.terminationDate = Timestamp.fromDate(updates.terminationDate);
      }

      await updateDoc(docRef, updateData);

      // Update cache
      const index = this.cachedStaff.findIndex(s => s.id === id);
      if (index !== -1) {
        this.cachedStaff[index] = {
          ...this.cachedStaff[index],
          ...updates,
          updatedAt: new Date()
        };
        this.saveToLocalStorage();
        // (No barbers-sync needed — this service now writes directly to the barbers collection.)

        toast.success('Staff member updated successfully');
        return this.cachedStaff[index];
      }

      return null;
    } catch (error) {
      console.error('Error updating staff:', error);
      toast.error('Failed to update staff member');
      throw error;
    }
  }

  /**
   * Delete staff member (soft delete - sets status to terminated)
   */
  async deleteStaff(id: string, permanent: boolean = false): Promise<void> {
    try {
      if (permanent) {
        await deleteDoc(doc(this.staffCollection, id));
        this.cachedStaff = this.cachedStaff.filter(s => s.id !== id);
      } else {
        await this.updateStaff(id, {
          status: 'terminated',
          terminationDate: new Date()
        });
      }

      this.saveToLocalStorage();
      toast.success('Staff member removed');
    } catch (error) {
      console.error('Error deleting staff:', error);
      toast.error('Failed to remove staff member');
      throw error;
    }
  }

  // ==================== QUERIES ====================

  /**
   * Get staff by status
   */
  getStaffByStatus(status: StaffStatus): StaffMember[] {
    return this.cachedStaff.filter(s => s.status === status);
  }

  /**
   * Get active staff
   */
  getActiveStaff(): StaffMember[] {
    return this.cachedStaff.filter(s => s.status === 'active');
  }

  /**
   * Get staff by role
   */
  getStaffByRole(role: StaffRole): StaffMember[] {
    // Case-insensitive: barber docs store roles capitalized ("Manager"/"Owner"), while callers
    // (e.g. checkout's discount-approval alert) pass lowercase. Match regardless of case.
    const want = String(role).toLowerCase();
    // Exclude archived/deleted so a departed manager doesn't get discount alerts (2026-08-02).
    return this.cachedStaff.filter(s =>
      String(s.role).toLowerCase() === want &&
      (s.status as string) !== 'inactive' && (s.status as string) !== 'deleted'
    );
  }

  /**
   * Get staff by career stage
   */
  getStaffByCareerStage(stage: CareerStage): StaffMember[] {
    return this.cachedStaff.filter(s => s.careerStage === stage);
  }

  /**
   * Filter staff members
   */
  filterStaff(filters: StaffFilters): StaffMember[] {
    let result = [...this.cachedStaff];

    if (filters.status?.length) {
      result = result.filter(s => filters.status!.includes(s.status));
    }

    if (filters.role?.length) {
      result = result.filter(s => filters.role!.includes(s.role));
    }

    if (filters.careerStage?.length) {
      result = result.filter(s => filters.careerStage!.includes(s.careerStage));
    }

    if (filters.department) {
      result = result.filter(s => s.department === filters.department);
    }

    if (filters.search) {
      const search = filters.search.toLowerCase();
      result = result.filter(s =>
        s.firstName.toLowerCase().includes(search) ||
        s.lastName.toLowerCase().includes(search) ||
        s.email.toLowerCase().includes(search)
      );
    }

    return result;
  }

  /**
   * Search staff
   */
  searchStaff(query: string): StaffMember[] {
    if (!query.trim()) return this.cachedStaff;

    const searchLower = query.toLowerCase();
    return this.cachedStaff.filter(s =>
      s.firstName.toLowerCase().includes(searchLower) ||
      s.lastName.toLowerCase().includes(searchLower) ||
      s.email.toLowerCase().includes(searchLower) ||
      s.phone.includes(query) ||
      s.specialties.some(sp => sp.toLowerCase().includes(searchLower))
    );
  }

  // ==================== STATISTICS ====================

  /**
   * Get staff statistics
   */
  getStatistics(): StaffStatistics {
    const staff = this.cachedStaff;
    const activeStaff = staff.filter(s => s.status === 'active');

    if (staff.length === 0) {
      return {
        totalStaff: 0,
        activeStaff: 0,
        onLeave: 0,
        avgRating: 0,
        avgCommission: 0,
        totalMonthlyRevenue: 0,
        avgBpiScore: 0,
        topPerformers: []
      };
    }

    const avgRating = activeStaff.length > 0
      ? activeStaff.reduce((sum, s) => sum + s.metrics.avgRating, 0) / activeStaff.length
      : 0;

    const avgCommission = activeStaff.length > 0
      ? activeStaff.reduce((sum, s) => sum + s.compensation.commissionRate, 0) / activeStaff.length
      : 0;

    const totalMonthlyRevenue = activeStaff.reduce((sum, s) => sum + s.metrics.monthlyRevenue, 0);

    const avgBpiScore = activeStaff.length > 0
      ? activeStaff.reduce((sum, s) => sum + s.metrics.bpiScore, 0) / activeStaff.length
      : 0;

    // Get top 3 performers by BPI score
    const topPerformers = [...activeStaff]
      .sort((a, b) => b.metrics.bpiScore - a.metrics.bpiScore)
      .slice(0, 3)
      .map(s => `${s.firstName} ${s.lastName}`);

    return {
      totalStaff: staff.length,
      activeStaff: activeStaff.length,
      onLeave: staff.filter(s => s.status === 'on-leave').length,
      avgRating: Number(avgRating.toFixed(1)),
      avgCommission: Math.round(avgCommission),
      totalMonthlyRevenue,
      avgBpiScore: Math.round(avgBpiScore),
      topPerformers
    };
  }

  // ==================== AVAILABILITY ====================

  /**
   * Update staff availability
   */
  async updateAvailability(staffId: string, availability: WeeklyAvailability): Promise<void> {
    await this.updateStaff(staffId, { availability });
  }

  /**
   * Get staff available on a specific day
   */
  getStaffAvailableOnDay(day: keyof WeeklyAvailability): StaffMember[] {
    return this.cachedStaff.filter(s =>
      s.status === 'active' && isDayEnabled(s.availability[day])
    );
  }

  // ==================== TIME OFF ====================

  /**
   * Submit time off request
   */
  async submitTimeOffRequest(request: Omit<TimeOffRequest, 'id' | 'status' | 'requestedAt'>): Promise<TimeOffRequest> {
    try {
      const now = Timestamp.now();
      const requestData = {
        ...request,
        status: 'pending' as const,
        requestedAt: now
      };

      const docRef = await addDoc(this.timeOffCollection, requestData);
      const newRequest: TimeOffRequest = {
        ...requestData,
        id: docRef.id,
        requestedAt: now
      };

      toast.success('Time off request submitted');
      return newRequest;
    } catch (error) {
      console.error('Error submitting time off request:', error);
      toast.error('Failed to submit time off request');
      throw error;
    }
  }

  /**
   * Get pending time off requests
   */
  async getPendingTimeOffRequests(): Promise<TimeOffRequest[]> {
    try {
      const q = query(
        this.timeOffCollection,
        where('status', '==', 'pending'),
        orderBy('requestedAt', 'desc')
      );
      const snapshot = await getDocs(q);

      return snapshot.docs.map(docSnapshot => ({
        id: docSnapshot.id,
        ...docSnapshot.data()
      } as TimeOffRequest));
    } catch (error) {
      console.error('Error fetching time off requests:', error);
      return [];
    }
  }

  /**
   * Approve/Deny time off request
   */
  async reviewTimeOffRequest(
    requestId: string,
    status: 'approved' | 'denied',
    reviewedBy: string,
    notes?: string
  ): Promise<void> {
    try {
      await updateDoc(doc(this.timeOffCollection, requestId), {
        status,
        reviewedBy,
        reviewedAt: serverTimestamp(),
        reviewNotes: notes
      });

      toast.success(`Time off request ${status}`);
    } catch (error) {
      console.error('Error reviewing time off request:', error);
      toast.error('Failed to update request');
      throw error;
    }
  }

  // ==================== DOCUMENTS ====================

  /**
   * Upload staff document
   */
  async uploadDocument(
    staffId: string,
    file: File,
    type: StaffDocument['type'],
    name: string
  ): Promise<StaffDocument> {
    try {
      const storageRef = ref(storage, `staff/${staffId}/documents/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);

      const docData = {
        staffId,
        type,
        name,
        url,
        uploadedAt: Timestamp.now()
      };

      const docRef = await addDoc(this.documentsCollection, docData);

      toast.success('Document uploaded successfully');
      return {
        id: docRef.id,
        ...docData,
        uploadedAt: docData.uploadedAt
      };
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error('Failed to upload document');
      throw error;
    }
  }

  /**
   * Upload staff photo
   */
  async uploadPhoto(staffId: string, file: File): Promise<string> {
    try {
      const storageRef = ref(storage, `staff/${staffId}/photo_${Date.now()}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);

      await this.updateStaff(staffId, { photo: url });
      return url;
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast.error('Failed to upload photo');
      throw error;
    }
  }

  // ==================== METRICS UPDATE ====================

  /**
   * Update staff metrics
   */
  async updateMetrics(staffId: string, metrics: Partial<StaffMetrics>): Promise<void> {
    const staff = await this.getStaffById(staffId);
    if (!staff) return;

    const updatedMetrics: StaffMetrics = {
      ...staff.metrics,
      ...metrics
    };

    await this.updateStaff(staffId, { metrics: updatedMetrics });
  }

  // ==================== LOCAL STORAGE ====================

  private saveToLocalStorage(): void {
    try {
      localStorage.setItem('slikpos_staff', JSON.stringify(this.cachedStaff));
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  }

  private loadFromLocalStorage(): void {
    try {
      const stored = localStorage.getItem('slikpos_staff');
      if (stored) {
        const parsed = JSON.parse(stored);
        this.cachedStaff = parsed.map((s: any) => ({
          ...s,
          hireDate: new Date(s.hireDate),
          terminationDate: s.terminationDate ? new Date(s.terminationDate) : undefined,
          createdAt: new Date(s.createdAt),
          updatedAt: new Date(s.updatedAt)
        }));
      }
    } catch (error) {
      console.error('Error loading from localStorage:', error);
    }
  }

  // ==================== SUBSCRIPTION ====================

  /**
   * Subscribe to staff changes
   */
  subscribe(callback: (staff: StaffMember[]) => void): () => void {
    const id = Math.random().toString(36).substring(7);

    const unsubscribe = onSnapshot(
      query(this.staffCollection, orderBy('lastName', 'asc')),
      (snapshot) => {
        const staffMembers: StaffMember[] = [];
        snapshot.forEach((docSnapshot) => {
          staffMembers.push(
            this.convertFirestoreToStaff(docSnapshot.id, docSnapshot.data())
          );
        });
        this.cachedStaff = staffMembers;
        callback(staffMembers);
      }
    );

    this.listeners.set(id, unsubscribe);

    // Return unsubscribe function
    return () => {
      const unsub = this.listeners.get(id);
      if (unsub) {
        unsub();
        this.listeners.delete(id);
      }
    };
  }

  /**
   * Get cached staff (synchronous)
   */
  getCachedStaff(): StaffMember[] {
    return this.cachedStaff;
  }
}

// Export singleton instance
export const staffService = StaffService.getInstance();

// Export class for type reference
export { StaffService };
